import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type IHttpConnectionOptions,
} from '@microsoft/signalr';
import { getSessionToken } from './apiClient';

const HUB_URL = (import.meta.env.VITE_API_BASE_URL ?? '') + '/hub/game';

let connection: HubConnection | null = null;
let connectingPromise: Promise<HubConnection> | null = null;
const handlers = new Map<string, Set<(payload: unknown) => void>>();
let onConnectionStateChange: (state: HubConnectionState) => void = () => {};
let onReconnectedCallback: (() => Promise<void>) | null = null;

export function setOnReconnected(cb: () => Promise<void>) {
  onReconnectedCallback = cb;
}

export function setConnectionStateListener(cb: (state: HubConnectionState) => void) {
  onConnectionStateChange = cb;
}

function buildConnection(): HubConnection {
  // Identity travels as the same per-tab sessionToken REST already uses (via
  // accessTokenFactory — sent as `Authorization: Bearer` on negotiate and as
  // `?access_token=` on the WebSocket/SSE upgrade itself, since browsers don't let
  // those transports set custom headers). withCredentials is kept as a fallback path
  // (skvoz_session cookie) for transports/edge-cases without a token, but the cookie
  // is shared by the whole browser, not per-tab — relying on it alone let a second
  // tab's guest registration silently steal an already-open tab's identity on
  // (re)connect (see docs/PROGRESS.md, "RoomStore.subscribe() падает на null").
  const options: IHttpConnectionOptions = {
    withCredentials: true,
    accessTokenFactory: () => getSessionToken() ?? '',
  };
  const conn = new HubConnectionBuilder()
    .withUrl(HUB_URL, options)
    .withAutomaticReconnect([0, 1000, 2000, 5000, 10000, 20000])
    .configureLogging(import.meta.env.DEV ? LogLevel.Information : LogLevel.Warning)
    .build();

  conn.onreconnecting(() => onConnectionStateChange(HubConnectionState.Reconnecting));
  conn.onreconnected(async () => {
    onConnectionStateChange(HubConnectionState.Connected);
    if (onReconnectedCallback) await onReconnectedCallback();
  });
  conn.onclose(() => onConnectionStateChange(HubConnectionState.Disconnected));

  for (const [event, callbacks] of handlers) {
    conn.on(event, (payload: unknown) => {
      callbacks.forEach((cb) => cb(payload));
    });
  }
  return conn;
}

export async function ensureConnected(): Promise<HubConnection> {
  if (!connection) connection = buildConnection();
  if (connection.state === HubConnectionState.Connected) return connection;
  if (connectingPromise) return connectingPromise;
  if (connection.state === HubConnectionState.Disconnected) {
    connectingPromise = (async () => {
      try {
        onConnectionStateChange(HubConnectionState.Connecting);
        await connection!.start();
        onConnectionStateChange(HubConnectionState.Connected);
        return connection!;
      } finally {
        connectingPromise = null;
      }
    })();
    return connectingPromise;
  }
  return connection;
}

export async function stop(): Promise<void> {
  if (connection) {
    await connection.stop();
    connection = null;
  }
}

export function on<T>(event: string, handler: (payload: T) => void): () => void {
  let set = handlers.get(event);
  if (!set) {
    set = new Set();
    handlers.set(event, set);
    connection?.on(event, (payload: unknown) => set!.forEach((cb) => cb(payload)));
  }
  set.add(handler as (p: unknown) => void);
  return () => {
    set!.delete(handler as (p: unknown) => void);
  };
}

export async function invoke<T>(method: string, ...args: unknown[]): Promise<T> {
  const conn = await ensureConnected();
  return conn.invoke<T>(method, ...args);
}
